import { CloudServerOutlined, SyncOutlined } from '@ant-design/icons';
import { Tooltip, theme } from 'antd';
import { useTranslation } from 'react-i18next';

import { useProcessingLock } from '@/contexts/processing-lock-context';
import { OPERATION_TYPE_KEY_MAP } from '@/features/batch-operations/utils';
import { useProgressRouter } from '@/hooks/use-progress-router';
import { useScope } from '@/hooks/use-scope';
import { useUrlPrefix } from '@/hooks/use-url-prefix';

interface BatchOperationsShortcutProps {
  isCollapsed: boolean;
}

export function BatchOperationsShortcut({
  isCollapsed,
}: BatchOperationsShortcutProps) {
  const router = useProgressRouter();
  const decorateUrl = useUrlPrefix();
  const { organizationSlug } = useScope();
  const { isLocked, operationType } = useProcessingLock();
  const { token } = theme.useToken();
  const { t } = useTranslation();

  // Only show in organization context
  if (!organizationSlug) return null;

  const handleClick = () => {
    router.push(decorateUrl('/batch-operations'));
  };

  const opKey =
    operationType && OPERATION_TYPE_KEY_MAP[operationType]
      ? OPERATION_TYPE_KEY_MAP[operationType]
      : 'default';
  const tooltipTitle = isLocked
    ? t('batchOperationsShortcut.processing', {
        operation: t(`batchOperationsShortcut.operationLabels.${opKey}`),
      })
    : t('batchOperationsShortcut.title');

  return (
    <Tooltip title={tooltipTitle} placement={isCollapsed ? 'right' : undefined}>
      <button
        onClick={handleClick}
        className="inline-flex cursor-pointer justify-center rounded p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white"
      >
        <span className="text-2xl">
          {isLocked ? (
            <SyncOutlined spin style={{ color: token.colorPrimary }} />
          ) : (
            <CloudServerOutlined />
          )}
        </span>
      </button>
    </Tooltip>
  );
}
